/* eslint-disable @typescript-eslint/no-explicit-any */
import { Autocomplete, Box, debounce, TextField } from "@mui/material";
import { grey } from "@mui/material/colors";
import { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { GooglePlacesAPI } from "../../api";
import {
  Address,
  addressToString,
  parseAddressFromString,
  validateAddress,
} from "../../model/Address";

interface AddressAutocompleteProps {
  onSubmit?: (address: Address | undefined) => void;
  onInsufficient?: () => void;
  label?: string;
  required?: boolean;
  strict?: boolean;
  variant?: "standard" | "filled" | "outlined";
  value?: Address;
  size?: "small" | "medium";
  condensed?: boolean;
}

type StructuredPlace = {
  id: string;
  first_line: string;
  suggestion: google.maps.places.AutocompleteSuggestion;
};

export const AddressAutocomplete: FC<AddressAutocompleteProps> = ({
  onSubmit,
  label,
  required,
  strict,
  variant,
  value,
  size,
  condensed = false,
  ...props
}) => {
  const EMPTY_SUGGESTION = useMemo<StructuredPlace>(
    () => ({
      id: "",
      first_line: value?.first_line || "",
      suggestion: new google.maps.places.AutocompleteSuggestion(),
    }),
    [value?.first_line],
  );

  const [suggestions, setSuggestions] = useState<Array<StructuredPlace>>([]);
  const [selectedSuggestion, setSelectedSuggestion] =
    useState<StructuredPlace | null>(EMPTY_SUGGESTION);
  const [input, setInput] = useState<string>(
    value ? addressToString(value) : "",
  );
  const [types, setTypes] = useState<Array<string>>(["geocode"]);
  const [color, setColor] = useState<"primary" | "error" | "success">(
    "primary",
  );
  const lastCanonicalAddress = useRef("");
  const [isUserEditing, setIsUserEditing] = useState(false);

  useEffect(() => {
    const addrStr = value ? addressToString(value) : "";
    if (!isUserEditing && addrStr !== lastCanonicalAddress.current) {
      setInput(addrStr);
      setSelectedSuggestion({
        ...EMPTY_SUGGESTION,
        first_line: addrStr,
      });
      lastCanonicalAddress.current = addrStr;
    }
  }, [EMPTY_SUGGESTION, value, isUserEditing]);

  useEffect(() => {
    setTypes(strict ? ["geocode"] : ["geocode", "establishment"]);
  }, [strict]);

  const fetch = useCallback(async () => {
    if (input.length <= 3) {
      setSuggestions([]);
      return;
    }
    try {
      const results = await GooglePlacesAPI.getAutocompleteSuggestionFromInput(
        input,
        types,
      );
      const arr: Array<StructuredPlace> = results
        .filter(
          (result) =>
            result.placePrediction &&
            result.placePrediction.mainText &&
            result.placePrediction.secondaryText &&
            result.placePrediction.placeId &&
            result.placePrediction.mainText.text &&
            result.placePrediction.secondaryText.text,
        )
        .map((result) => {
          const place = result.placePrediction!;
          return {
            id: place.placeId!,
            first_line: `${place.mainText!.text}, ${place.secondaryText!.text}`,
            suggestion: result,
          };
        });
      setSuggestions(arr);
    } catch (error) {
      console.error("Error fetching autocomplete suggestions:", error);
    }
  }, [input, types]);

  const debouncedFetch = useMemo(() => debounce(fetch, 200), [fetch]);

  useEffect(() => {
    if (input.length > 3) {
      debouncedFetch();
    } else {
      setSuggestions([]);
    }
    if (!input) {
      setSelectedSuggestion(null);
      setColor("primary");
    }
  }, [input, debouncedFetch, EMPTY_SUGGESTION]);

  const handleInputChange = (_: any, newValue: string, reason: string) => {
    setIsUserEditing(true);
    if (reason === "clear") {
      setInput("");
      setSelectedSuggestion(null);
      setIsUserEditing(false);
      onSubmit?.(undefined);
    } else {
      setInput(newValue);
      if (condensed) {
        const manualAddress = parseAddressFromString(newValue);
        if (!manualAddress) {
          onSubmit?.(undefined);
          return;
        }
        if (validateAddress(manualAddress)) {
          onSubmit?.(manualAddress);
        } else {
          onSubmit?.(undefined);
        }
      } else {
        onSubmit?.(undefined);
      }
    }
  };

  const handleChange = (_: any, newValue: StructuredPlace | string | null) => {
    setIsUserEditing(false);
    if (newValue && typeof newValue !== "string") {
      GooglePlacesAPI.getSuggestionAsAddress(newValue.suggestion).then(
        (res) => {
          if (!res) {
            props.onInsufficient?.();
            return;
          }
          const fullAddress = addressToString(res);
          setInput(fullAddress);
          setSelectedSuggestion({
            ...newValue,
            first_line: fullAddress,
          });
          onSubmit?.(res);
        },
      );
    }
  };

  const handleBlur = () => {
    setIsUserEditing(false);
    if (condensed && input && (!selectedSuggestion || !selectedSuggestion.id)) {
      const manualAddress = parseAddressFromString(input);
      if (!manualAddress) return;
      onSubmit?.(manualAddress);
    }
  };

  return (
    <Autocomplete
      freeSolo
      inputValue={input}
      value={input ? selectedSuggestion : null}
      autoHighlight
      options={input.length > 3 ? suggestions : []}
      onInputChange={handleInputChange}
      onChange={handleChange}
      onBlur={handleBlur}
      renderInput={(params) => (
        <TextField
          {...params}
          required={required}
          color={color}
          size={size}
          label={label}
          variant={variant || "outlined"}
        />
      )}
      renderOption={(props, option) => {
        const [main, ...rest] = option.first_line.split(",");
        const secondary = rest.join(",").trim();
        return (
          <Box {...props} key={option.id} component="li">
            <span style={{ fontWeight: 500 }}>{main.trim()}</span>
            {secondary && (
              <span style={{ color: grey[600], fontWeight: 400 }}>
                {`, ${secondary}`}
              </span>
            )}
          </Box>
        );
      }}
      getOptionLabel={(option) =>
        typeof option === "string" ? option : option.first_line
      }
      isOptionEqualToValue={(option, value) =>
        option.id === value.id && option.first_line === value.first_line
      }
    />
  );
};
